import { createRegisterableComponent } from '@news-mono/web-common'
import React from 'react'
import {
    CommonwealthGamesMedalTallyWidgetColorMode,
    CommonwealthGamesMedalTallyWidget,
    CommonwealthGamesMedalTallyWidgetSize,
} from './CommonwealthGamesMedalTallyWidget'
import {
    CommonwealthGamesMedalTallyRow,
    mapMedalTallyResponse,
} from './CommonwealthGamesMedalTallyWidget.data'

const MEDALS_URL = '/medals'

export interface CommonwealthGamesMedalTallyWidgetRouteProps {
    maxRows?: number
    fullMedalTallyUrl?: string
    refreshIntervalSeconds?: number
    colorMode?: CommonwealthGamesMedalTallyWidgetColorMode
    sortable?: boolean
    size?: CommonwealthGamesMedalTallyWidgetSize
    includeTotal?: boolean
}

const CommonwealthGamesMedalTallyWidgetContainer: React.FC<CommonwealthGamesMedalTallyWidgetRouteProps> =
    ({
        maxRows,
        fullMedalTallyUrl,
        refreshIntervalSeconds,
        colorMode,
        sortable,
        size,
        includeTotal,
    }) => {
        const [loading, setLoading] = React.useState(true)
        const [error, setError] = React.useState<string | null>(null)
        const [rows, setRows] = React.useState<
            CommonwealthGamesMedalTallyRow[]
        >([])

        React.useEffect(() => {
            let isMounted = true
            let hasLoadedSuccessfully = false

            const load = async (isInitialLoad = false) => {
                try {
                    const response = await fetch(MEDALS_URL)
                    const text = await response.text()

                    if (!response.ok) {
                        throw new Error(
                            `Request failed with status ${response.status}`,
                        )
                    }

                    if (isMounted) {
                        setRows(mapMedalTallyResponse(text))
                        setError(null)
                        hasLoadedSuccessfully = true
                    }
                } catch (e) {
                    if (
                        isMounted &&
                        (isInitialLoad || !hasLoadedSuccessfully)
                    ) {
                        setError(
                            e instanceof Error
                                ? e.message
                                : 'Failed to load medal tally data',
                        )
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false)
                    }
                }
            }

            load(true)

            const refreshInterval =
                typeof refreshIntervalSeconds === 'number' &&
                refreshIntervalSeconds > 0
                    ? window.setInterval(() => {
                          void load(false)
                      }, refreshIntervalSeconds * 1000)
                    : null

            return () => {
                isMounted = false
                if (refreshInterval !== null) {
                    window.clearInterval(refreshInterval)
                }
            }
        }, [refreshIntervalSeconds])

        return (
            <CommonwealthGamesMedalTallyWidget
                loading={loading}
                error={error}
                rows={rows}
                maxRows={maxRows}
                fullMedalTallyUrl={fullMedalTallyUrl}
                colorMode={colorMode}
                sortable={sortable}
                size={size}
                includeTotal={includeTotal}
            />
        )
    }

export const CommonwealthGamesMedalTallyWidgetRegistration =
    createRegisterableComponent(
        'commonwealth-games-medal-tally-widget',
        (props: CommonwealthGamesMedalTallyWidgetRouteProps) => (
            <CommonwealthGamesMedalTallyWidgetContainer {...props} />
        ),
    )
